import type { ScaleBand } from 'd3-scale';

import { YTickWithTruncation } from './YTickWithTruncation.js';
import { AxisLeft } from '../../../core/components/axes/AxisLeft.js';
import { DivAxisWrapper } from '../../../core/components/axes/components/DivAxisWrapper.js';
import { axisFontProps } from '../../../core/components/axes/constants.js';
import { getYTickMaxSize } from '../../../core/components/axes/utils/calculate-axis-size.js';
import { getTickLabelPropsForValueAxis } from '../../../core/components/axes/utils/get-tick-label-props.js';
import { isTickLabelSpaceAvailable } from '../../../core/components/axes/utils/is-tick-label-visible.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { useAxesTicks } from '../../hooks/useAxesTicks.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { isCategorical } from '../../types/guards.js';
import { AREAS } from '../../utils/grid-templates.js';

const tickLabelProps = getTickLabelPropsForValueAxis('left');

export function XYChartLeftAxisArea() {
  const { leftTicks } = useAxesTicks();
  const { left: leftConfig } = useVisibleAxesConfig();
  const { left: leftScale } = useVisibleAxesScales();

  const tickFormat = (_: unknown, index: number) =>
    leftTicks.formattedTicks[index];

  const isCategoricalAxis = leftConfig?.type
    ? isCategorical(leftConfig.type)
    : false;

  const areCategoricalTicksNotFittable =
    leftScale &&
    isCategoricalAxis &&
    !isTickLabelSpaceAvailable((leftScale as ScaleBand<string>).step());

  const hideAxis =
    leftConfig === undefined ||
    areCategoricalTicksNotFittable ||
    leftScale === undefined;

  return hideAxis ? null : (
    <ChartGrid.GridArea gridArea={AREAS.leftAxis}>
      {({ availableWidth, availableHeight }) => (
        <DivAxisWrapper
          divAxisStyle={axisFontProps}
          ticks={leftTicks.formattedTicks}
          label={leftConfig.label}
        >
          <AxisLeft
            data-testid="value-axis-left"
            scale={leftScale}
            containerSize={availableHeight}
            width={availableWidth}
            tickValues={leftTicks.ticks}
            tickLabelProps={tickLabelProps}
            tickFormat={tickFormat}
            label={leftConfig.label}
            tickComponent={(renderProps) => (
              <YTickWithTruncation
                position="left"
                renderProps={renderProps}
                maxSpaceForTruncation={getYTickMaxSize(
                  availableWidth,
                  leftConfig.label,
                )}
                centerTick={isCategoricalAxis}
              />
            )}
          />
        </DivAxisWrapper>
      )}
    </ChartGrid.GridArea>
  );
}

XYChartLeftAxisArea['displayName'] = 'XYChartLeftAxisArea';
